// components/SmartImage.tsx
import * as React from "react";

/** Valid CSS object-fit values */
type CssObjectFit = "cover" | "contain" | "fill" | "none" | "scale-down";

/** Optional Cloudinary intent values we’ll map to CSS */
type CloudinaryFitIntent = CssObjectFit | "inside" | "outside";

type CommonProps = {
  alt: string;
  className?: string;
  /** Example: "(max-width: 768px) 100vw, 768px" */
  sizes?: string;
  /** Width candidates to build srcset */
  widths?: number[];
  width?: number;
  height?: number;
  /** Above‑the‑fold? Preload and eager decode. */
  priority?: boolean;
  /** Only affects Cloudinary branch */
  placeholder?: "blur" | "empty";
  /** Visual fit (CSS). If using "inside"/"outside", we map to contain/cover for CSS */
  fit?: CloudinaryFitIntent;
  /** Direct CSS object-fit override (takes precedence over `fit`) */
  objectFit?: CssObjectFit;
};

type CloudinaryCommon = {
  /** Optional overrides for common transforms */
  crop?: "fill" | "fill_pad" | "fit" | "scale" | "thumb";
  gravity?: "auto" | "center" | string;
  /** Extra transforms to append/merge (e.g., ["ar_16:9","dpr_2","e_sharpen"]) */
  extraTransforms?: string[];
};

type CloudinaryByIdProps = CommonProps &
  CloudinaryCommon & {
    cldPublicId: string; // e.g. "samples/landscapes/beach"
    cldCloudName: string; // your Cloudinary cloud name
  };

type CloudinaryByUrlProps = CommonProps &
  CloudinaryCommon & {
    /** Full Cloudinary URL to parse */
    cldUrl: string | undefined;
  };

type LocalProps = CommonProps & {
  /** Base file path under /public (the original/master) */
  src: string; // e.g. "/images/hero.jpg"
  /** If your prebuilt variants use a base like /images/hero-640.* */
  localBasename?: string; // defaults to src filename (no extension)
  localExt?: string; // defaults to src's extension
};

type Props = CloudinaryByIdProps | CloudinaryByUrlProps | LocalProps;

const defaultWidths = [320, 480, 640, 768, 1024, 1280, 1536, 1920];

/** Map Cloudinary-ish intent to valid CSS object-fit */
const toCssFit = (fit?: CloudinaryFitIntent): CssObjectFit | undefined => {
  if (!fit) return undefined;
  if (fit === "inside") return "contain";
  if (fit === "outside") return "cover";
  return fit;
};

type ParsedCloudinaryUrl = {
  cloudName: string;
  resourceType: "image" | "video" | "raw";
  deliveryType: "upload" | string;
  transforms: string[]; // tokens like "f_auto", "q_auto", "c_fill", "g_auto", "w_500", ...
  version?: string; // e.g. "v1701600322"
  publicId: string; // e.g. "PrimaCura/Courses/socialCare/FAW_p9srhv.jpg"
};

function parseCloudinaryUrl(url: string): ParsedCloudinaryUrl | null {
  try {
    const u = new URL(url);
    // Expected: https://res.cloudinary.com/{cloud}/image/upload/{transforms?}/{version?}/{publicId...}
    const parts = u.pathname.replace(/^\/+/, "").split("/");
    const [cloudName, resourceType, deliveryType, ...rest] = parts;
    if (!cloudName || !resourceType || !deliveryType) return null;

    let idx = 0;
    let transforms: string[] = [];
    let version: string | undefined;

    if (rest.length > 0) {
      const first = rest[0];
      if (first && first.includes(",")) {
        transforms = first.split(",").filter(Boolean);
        idx = 1;
      }
      if (rest[idx] && /^v\d+/.test(rest[idx])) {
        version = rest[idx];
        idx += 1;
      }
    }

    const publicId = rest.slice(idx).join("/");

    return {
      cloudName,
      resourceType: (resourceType as any) || "image",
      deliveryType: (deliveryType as any) || "upload",
      transforms,
      version,
      publicId,
    };
  } catch {
    return null;
  }
}

/** Merge/override transforms. Ensures we always emit one w_… (per width). */
function buildTransformString(
  baseTransforms: string[],
  {
    crop,
    gravity,
    extraTransforms,
    widthForSrcset,
  }: {
    crop?: string;
    gravity?: string;
    extraTransforms?: string[];
    widthForSrcset: number;
  }
) {
  const map = new Map<string, string>();
  for (const t of baseTransforms) {
    const [k, v] = t.split("_", 2);
    if (!k) continue;
    if (v === undefined) {
      map.set(t, "");
    } else {
      map.set(k, v);
    }
  }

  if (!map.has("f")) map.set("f", "auto");
  if (!map.has("q")) map.set("q", "auto");
  if (crop) map.set("c", crop);
  if (gravity) map.set("g", gravity);

  map.set("w", String(widthForSrcset));

  if (extraTransforms?.length) {
    for (const tok of extraTransforms) {
      const [k, v] = tok.split("_", 2);
      if (!k) continue;
      if (v === undefined) {
        map.set(tok, "");
      } else {
        map.set(k, v);
      }
    }
  }

  const tokens: string[] = [];
  for (const [k, v] of map) {
    if (v === "" && !["f", "q", "c", "g", "w", "h", "ar", "dpr"].includes(k)) {
      tokens.push(k);
    } else {
      tokens.push(`${k}_${v}`);
    }
  }

  return tokens.join(",");
}

/** Build a Cloudinary URL from pieces */
function makeCloudinaryUrl(
  cloudName: string,
  deliveryType: string,
  transforms: string,
  publicId: string,
  version?: string,
  resourceType: string = "image"
) {
  const ver = version ? `/${version}` : "";
  const t = transforms ? `/${transforms}` : "";
  return `https://res.cloudinary.com/${cloudName}/${resourceType}/${deliveryType}${t}${ver}/${publicId}`;
}

export function SmartImage(props: Props) {
  const {
    alt,
    className,
    sizes = "(max-width: 768px) 100vw, 768px",
    widths = defaultWidths,
    width,
    height,
    priority,
    placeholder = "empty",
    fit,
    objectFit, // NEW: direct CSS object-fit override
  } = props as CommonProps;

  const loading: "eager" | "lazy" = priority ? "eager" : "lazy";
  const decoding: "sync" | "async" | "auto" = priority ? "sync" : "async";
  const fetchPriority: "high" | "low" | "auto" = priority ? "high" : "auto";

  // Precedence: explicit objectFit > fit (mapped)
  const cssFit = objectFit ?? toCssFit(fit);

  // --- Cloudinary via URL mode ---
  if ("cldUrl" in props && props.cldUrl) {
    const parsed = parseCloudinaryUrl(props.cldUrl);
    if (parsed) {
      const {
        cloudName,
        resourceType,
        deliveryType,
        transforms,
        version,
        publicId,
      } = parsed;

      const srcFor = (w: number) =>
        makeCloudinaryUrl(
          cloudName,
          deliveryType,
          buildTransformString(transforms, {
            crop: props.crop,
            gravity: props.gravity,
            extraTransforms: props.extraTransforms,
            widthForSrcset: w,
          }),
          publicId,
          version,
          resourceType
        );

      const src = srcFor(Math.min(...widths));
      const srcSet = widths.map((w) => `${srcFor(w)} ${w}w`).join(", ");

      const blurSrc =
        placeholder === "blur"
          ? makeCloudinaryUrl(
              cloudName,
              deliveryType,
              buildTransformString(transforms, {
                crop: props.crop,
                gravity: props.gravity,
                extraTransforms: [
                  ...(props.extraTransforms ?? []),
                  "q_20",
                  "w_20",
                  "e_blur:1000",
                ],
                widthForSrcset: 20,
              }),
              publicId,
              version,
              resourceType
            )
          : undefined;

      return (
        <img
          className={className}
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading={loading}
          decoding={decoding}
          fetchPriority={fetchPriority}
          width={width}
          height={height}
          style={{
            objectFit: cssFit,
            background:
              blurSrc && placeholder === "blur"
                ? `center / cover no-repeat url("${blurSrc}")`
                : undefined,
          }}
        />
      );
    }
    // Fallback: plain <img> with provided URL
    return (
      <img
        className={className}
        src={props.cldUrl}
        alt={alt}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        width={width}
        height={height}
        style={{ objectFit: cssFit }}
      />
    );
  }

  // --- Cloudinary by publicId/cloudName mode ---
  if ("cldPublicId" in props) {
    const {
      cldPublicId,
      cldCloudName,
      crop = "fill",
      gravity = "auto",
      extraTransforms,
    } = props;

    const base = ["f_auto", "q_auto", `c_${crop}`, `g_${gravity}`];
    if (extraTransforms?.length) base.push(...extraTransforms);

    const makeUrl = (w: number) =>
      `https://res.cloudinary.com/${cldCloudName}/image/upload/${buildTransformString(
        base,
        { widthForSrcset: w }
      )}/${cldPublicId}`;

    const src = makeUrl(Math.min(...widths));
    const srcSet = widths.map((w) => `${makeUrl(w)} ${w}w`).join(", ");

    const blurSrc =
      placeholder === "blur"
        ? `https://res.cloudinary.com/${cldCloudName}/image/upload/${buildTransformString(
            base,
            {
              widthForSrcset: 20,
              extraTransforms: ["q_20", "w_20", "e_blur:1000"],
            }
          )}/${cldPublicId}`
        : undefined;

    return (
      <img
        className={className}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        width={width}
        height={height}
        style={{
          objectFit: cssFit,
          background:
            blurSrc && placeholder === "blur"
              ? `center / cover no-repeat url("${blurSrc}")`
              : undefined,
        }}
      />
    );
  }

  // --- Local static mode ---
  if ("src" in props) {
    const { src, localBasename, localExt } = props;

    const lastDot = src.lastIndexOf(".");
    const ext = localExt ?? (lastDot > -1 ? src.slice(lastDot + 1) : "jpg");
    const base =
      localBasename ?? (lastDot > -1 ? src.slice(0, lastDot).replace(/^\/+/, "") : src);

    // compute base under img/_gen (customize to your structure)
    const baseRel = base.replace(/^img\//, "img/_gen/");
    const set = (format: string) =>
      (props.widths ?? defaultWidths)
        .map((w) => `/${baseRel}-${w}.${format} ${w}w`)
        .join(", ");

    const fallback = src;

    return (
      <picture>
        <source type="image/avif" srcSet={set("avif")} sizes={sizes} />
        <source type="image/webp" srcSet={set("webp")} sizes={sizes} />
        <img
          className={className}
          src={fallback}
          srcSet={set(ext)}
          sizes={sizes}
          alt={alt}
          loading={loading}
          decoding={decoding}
          fetchPriority={fetchPriority}
          width={width}
          height={height}
          style={{ objectFit: cssFit }}
        />
      </picture>
    );
  }

  return null;
}
